// UtilityButton — `.ub` family used for row actions, dropdown triggers, and
// inline controls. Mirrors lines 172–204 of
// /Users/harry/Downloads/employee-portal/project/styles.css.
//
// Combinations: size (sm/md) × treatment (dropshadow/subtle) × sentiment
// (neutral/positive/negative/warning/azurite). Add `iconOnly` for a square
// 24×24 (sm) or 32×32 (md).

import { Button, ButtonProps as ChakraButtonProps } from "@chakra-ui/react";
import { forwardRef, ReactElement } from "react";

export type UbSize = "sm" | "md";
export type UbTreatment = "dropshadow" | "subtle";
export type UbSentiment = "neutral" | "positive" | "negative" | "warning" | "azurite";

export interface UtilityButtonProps
  extends Omit<ChakraButtonProps, "variant" | "size" | "leftIcon" | "rightIcon"> {
  size?: UbSize;
  treatment?: UbTreatment;
  sentiment?: UbSentiment;
  iconOnly?: boolean;
  leftIcon?: ReactElement;
  rightIcon?: ReactElement;
}

// Sentiment maps only set color — see CSS lines 199–204.
const sentimentColor: Record<UbSentiment, string> = {
  neutral: "neutral.950",
  positive: "success.700",
  negative: "error.700",
  warning: "warning.700",
  azurite: "p.700",
};

const UtilityButton = forwardRef<HTMLButtonElement, UtilityButtonProps>(
  (
    {
      size = "md",
      treatment = "dropshadow",
      sentiment = "neutral",
      iconOnly = false,
      leftIcon,
      rightIcon,
      children,
      isDisabled,
      ...rest
    },
    ref,
  ) => {
    // Size: sm (24×24, 12px) vs md (32×32, 13px). icon-only kills horizontal padding.
    const sizeProps =
      size === "sm"
        ? {
            h: "24px",
            minW: "24px",
            borderRadius: "component.md", // 6px
            fontSize: "xs", // 12px
            px: iconOnly ? 0 : "1xs", // 6px
            w: iconOnly ? "24px" : undefined,
          }
        : {
            h: "32px",
            minW: "32px",
            borderRadius: "component.lg", // 8px
            fontSize: "smd", // 13px
            px: iconOnly ? 0 : "10px",
            w: iconOnly ? "32px" : undefined,
          };

    // Azurite sentiment overrides the dropshadow/subtle hover bg with p.50
    // (see CSS line 204: `.ub.sent-azurite:hover { background: var(--azurite-50); }`).
    const azuriteHoverOverride = sentiment === "azurite" ? { bg: "p.50" } : {};

    const treatmentProps =
      treatment === "dropshadow"
        ? {
            bg: "neutral.25",
            boxShadow: "elevLow",
            _hover: {
              bg: "neutral.75",
              boxShadow: "elevHigh",
              ...azuriteHoverOverride,
              _disabled: {
                bg: "neutral.75",
                boxShadow: "none",
              },
            },
          }
        : {
            bg: "transparent",
            boxShadow: "none",
            _hover: {
              bg: "neutral.100",
              ...azuriteHoverOverride,
              _disabled: {
                bg: "neutral.75",
              },
            },
          };

    return (
      <Button
        ref={ref}
        isDisabled={isDisabled}
        // `.ub` base — lines 172–179 of styles.css.
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        gap="1xs" // 6px
        boxSizing="border-box"
        cursor="pointer"
        fontFamily="body"
        fontWeight={500}
        lineHeight="1"
        color={sentimentColor[sentiment]}
        border="none"
        whiteSpace="nowrap"
        // Specific properties only (never `transition: all`). Strong ease-out
        // curve for the press feedback.
        transition="background-color 120ms, box-shadow 120ms, color 120ms, transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
        // Scale on press — tactile feedback. 0.96 (never below 0.95).
        _active={{ transform: "scale(0.96)" }}
        // Disabled — line 195–198.
        _disabled={{
          bg: "neutral.75",
          color: "neutral.500",
          boxShadow: "none",
          cursor: "not-allowed",
          opacity: 1,
          transform: "none",
        }}
        {...sizeProps}
        {...treatmentProps}
        {...rest}
      >
        {leftIcon}
        {children}
        {rightIcon}
      </Button>
    );
  },
);

UtilityButton.displayName = "UtilityButton";

export default UtilityButton;
